import type { RouteRecordRaw } from 'vue-router'
import { routes } from '@app/router/routes'

// One record per queue, generated in the same order as the sidebar. The queue
// registry (`constants/listingRequestQueues.ts`) owns paths + names; this file
// only binds them to page components, so adding a queue is a registry row plus
// a page file.
const QUEUE_ROUTE_META = {
  requiresAuth: true,
  permission: 'view_properties',
  // Must match the sidebar item's label in AppSidebar.
  activeMenu: 'Sell Requests',
} as const

export const listingRequestsRoutes: RouteRecordRaw[] = [
  // Module root → the review queue. Sidebar's parent item points here.
  {
    path: routes.LISTING_REQUESTS,
    redirect: { name: 'LISTING_REQUESTS_REVIEW' },
  },

  // ── Work queues ────────────────────────────────────────────────────────────
  {
    path: routes.LISTING_REQUESTS_REVIEW,
    name: 'LISTING_REQUESTS_REVIEW',
    component: () => import('./pages/ListingRequestsReviewPage.vue'),
    meta: QUEUE_ROUTE_META,
  },
  {
    path: routes.LISTING_REQUESTS_OWNERS_ONBOARDING,
    name: 'LISTING_REQUESTS_OWNERS_ONBOARDING',
    component: () => import('./pages/ListingRequestsOwnersOnboardingPage.vue'),
    meta: QUEUE_ROUTE_META,
  },
  {
    path: routes.LISTING_REQUESTS_AGREEMENTS,
    name: 'LISTING_REQUESTS_AGREEMENTS',
    component: () => import('./pages/ListingRequestsAgreementsPage.vue'),
    meta: QUEUE_ROUTE_META,
  },
  {
    path: routes.LISTING_REQUESTS_FINAL_APPROVAL,
    name: 'LISTING_REQUESTS_FINAL_APPROVAL',
    component: () => import('./pages/ListingRequestsFinalApprovalPage.vue'),
    meta: QUEUE_ROUTE_META,
  },

  // UAE Pass's return target for the platform counter-signature. Three static
  // segments, so it never collides with `/listing-requests/:id`. Behind the
  // normal auth guard on purpose — the signature is applied as the logged-in
  // admin, and an unauthenticated return has nobody to attribute it to.
  {
    path: routes.LISTING_REQUESTS_AGREEMENT_SIGN_RETURN,
    name: 'LISTING_REQUESTS_AGREEMENT_SIGN_RETURN',
    component: () => import('./pages/AgreementSignReturnPage.vue'),
    meta: QUEUE_ROUTE_META,
  },

  // ── Awaiting seller ────────────────────────────────────────────────────────
  {
    path: routes.LISTING_REQUESTS_REVISION_REQUESTED,
    name: 'LISTING_REQUESTS_REVISION_REQUESTED',
    component: () => import('./pages/ListingRequestsRevisionRequestedPage.vue'),
    meta: QUEUE_ROUTE_META,
  },

  // ── Directory ──────────────────────────────────────────────────────────────
  {
    path: routes.LISTING_REQUESTS_ALL,
    name: 'LISTING_REQUESTS_ALL',
    component: () => import('./pages/ListingRequestsAllPage.vue'),
    meta: QUEUE_ROUTE_META,
  },
  {
    path: routes.LISTING_REQUESTS_PROMOTED,
    name: 'LISTING_REQUESTS_PROMOTED',
    component: () => import('./pages/ListingRequestsPromotedPage.vue'),
    meta: QUEUE_ROUTE_META,
  },
  {
    path: routes.LISTING_REQUESTS_REJECTED,
    name: 'LISTING_REQUESTS_REJECTED',
    component: () => import('./pages/ListingRequestsRejectedPage.vue'),
    meta: QUEUE_ROUTE_META,
  },
  {
    path: routes.LISTING_REQUESTS_WITHDRAWN,
    name: 'LISTING_REQUESTS_WITHDRAWN',
    component: () => import('./pages/ListingRequestsWithdrawnPage.vue'),
    meta: QUEUE_ROUTE_META,
  },

  // The record — every queue's drill-in, and the only surface that acts on a
  // request. Registered after the queues; vue-router ranks the static queue
  // segments above `:id` regardless of order, so `/listing-requests/review`
  // never resolves as an id.
  {
    path: routes.LISTING_REQUEST_DETAIL,
    name: 'LISTING_REQUEST_DETAIL',
    component: () => import('./pages/ListingRequestDetailPage.vue'),
    meta: QUEUE_ROUTE_META,
  },

  // Redirects from retired paths — the pre-move `/properties/listing-requests*`
  // URLs and the agreements workspace that became a tab — so bookmarks and
  // in-flight links keep working. Safe to delete once analytics show no traffic
  // on them.
  {
    path: routes.LEGACY_PROPERTIES_LISTING_REQUESTS,
    redirect: { name: 'LISTING_REQUESTS_REVIEW' },
  },

  // The Owner Verification queue was folded into Review — the reviewer verifies
  // the declared owners and approves in one sitting, so there is no queue
  // between the two any more.
  {
    path: routes.LEGACY_LISTING_REQUESTS_OWNER_VERIFICATION,
    redirect: { name: 'LISTING_REQUESTS_REVIEW' },
  },
  {
    path: routes.LEGACY_PROPERTIES_LISTING_REQUEST_DETAIL,
    redirect: (to) => ({ name: 'LISTING_REQUEST_DETAIL', params: { id: to.params.id } }),
  },

  // The agreements workspace was folded into the record page's Agreements tab.
  // Declared before nothing in particular — it is a static suffix under `:id`,
  // which vue-router ranks above the bare record route on its own.
  {
    path: routes.LEGACY_LISTING_REQUEST_AGREEMENTS_WORKSPACE,
    redirect: (to) => ({
      name: 'LISTING_REQUEST_DETAIL',
      params: { id: to.params.id },
      query: { tab: 'agreements' },
    }),
  },
]
